import React, { useEffect, useState } from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';
import { Button, Flex, Label } from 'workspace-core-ui';
import { useAppDispatch, useAppSelector } from '@hooks/redux-hooks';
import css from '@styled-system/css';
import { actions as routeActions } from '@slices/routeSlice';
import { actions as levellingActions } from '@slices/levellingSlice';
import { COUNTABLE_SCREENS, WINDOW_HASH } from '@sharedConstants';
import { gameHasStarted, playSound, resetGame } from '@slices/gameStateSlice';
import {
  logItem,
  startNewSession,
  startNewPairShareSession,
} from '@slices/loggingSlice';
import routeMap from '@content/routemap.json';
import useTranslation from '@hooks/useTranslation';
import { selectProfile, setCurrentModule } from '../module/profile-slice';
import { Module as ModuleConfig } from '../../types';
import { History } from '../module/history';
import { AllowedScores, Module } from '../module/module';
import { Score } from './score';
import { Lock } from './Lock';

const ModuleButton = styled(Button).attrs({
  forwardedAs: Link,
})(
  ({ replayMode }: { replayMode: boolean }) => css({
    alignSelf: 'center',
    textDecoration: 'none',
    my: 2,
    maxWidth: replayMode ? '200px' : '500px',
    width: '90vw'
  }),
);

type ModuleOptionProps = {
  replayMode: boolean;
  module: ModuleConfig;
};

export const ModuleOption = (props: ModuleOptionProps): JSX.Element => {
  const { allRoutes } = useAppSelector(state => state.route);
  const { userId } = useAppSelector(state => state.logging);
  const dispatch = useAppDispatch();
  const profile = useAppSelector(selectProfile);
  const [isLocked, setIsLocked] = useState<boolean>(true);
  const [moduleScore, setModuleScore] = useState<AllowedScores | null>(null);
  const { t } = useTranslation();
  const { currentLanguage, isPairShareEnabled } = useAppSelector(state => state.gameState);
  useEffect(() => {
    if (userId) {
      const history = History.getHistory(userId);
      const record = History.getRecord(history, props.module.Id);
      const locked = Module.isLocked(props.module, history);
      setIsLocked(locked);
      if (record) {
        setModuleScore(record.starsAwarded as AllowedScores);
      }
    }
  }, []);

  function handlePress() {
    if (props.replayMode) {
      dispatch(resetGame());
      if (isPairShareEnabled === true) {
        dispatch(startNewPairShareSession({ language: currentLanguage }));
      } else {
        dispatch(startNewSession({ language: currentLanguage }));
      }
    }
    dispatch(setCurrentModule({
      module: props.module,
    }));
    dispatch(routeActions.setCurrentStream({
      streamName: props.module.Stream,
    }));
    const moduleRoutes = routeMap[props.module.Stream];
    dispatch(routeActions.initializeRoutes({
      routes: moduleRoutes,
      currentStreamName: props.module.Stream,
      profile,
    }));
    dispatch(
      levellingActions.setTotalClaims({
        newTotalClaims: moduleRoutes.filter(e =>
          COUNTABLE_SCREENS.includes(e.typeOfScreen),
        ).length,
      }),
    );
    dispatch(logItem({
      collection_name: 'events',
      event_type: 'click',
      target: `${props.module.Name} button`,
    }));
    playSound('Button');
    dispatch(gameHasStarted());
  }

  return (
    <ModuleButton
      data-cy="start"
      state={{ cameFromEnd: false }}
      isDisabled={isLocked}
      to={allRoutes[0].url + WINDOW_HASH}
      onPress={() => handlePress()}
      variant={props.replayMode ? 'secondary' : 'primary'}
      replayMode={props.replayMode}
    >
      {props.replayMode ? <Flex justifyContent="space-between" alignItems="center" >
        <Label variant='l1' >
          {t('Replay')}
        </Label>
      </Flex> : <Flex justifyContent="space-between" alignItems="center" style={{ maxWidth: '400px', width: '75vw' }}>
        <Label variant='l1' style={{ paddingRight: '15px' }}>
          {t(props.module.Name)}
        </Label>
        {(isLocked ? <Lock /> : <Score variant="history" points={moduleScore} />)}
      </Flex>}

    </ModuleButton>
  );
};
